import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "./supabaseClient";

const AuthContext = createContext({
  user: null,
  profile: null,
  loading: true,
  signInWithGoogle: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId, accessToken) => {
    if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return null;
    try {
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/profiles?select=*&id=eq.${userId}`,
        {
          headers: {
            "apikey": SUPABASE_ANON_KEY,
            "Authorization": `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        console.error("Profile fetch failed:", response.status);
        return null;
      }

      const data = await response.json();

      if (data.length === 0) {
        // Profile doesn't exist, create one
        const createResponse = await fetch(
          `${SUPABASE_URL}/rest/v1/profiles`,
          {
            method: "POST",
            headers: {
              "apikey": SUPABASE_ANON_KEY,
              "Authorization": `Bearer ${accessToken}`,
              "Content-Type": "application/json",
              "Prefer": "return=representation",
            },
            body: JSON.stringify({ id: userId, tier: "free" }),
          }
        );
        if (createResponse.ok) {
          const newData = await createResponse.json();
          return newData[0] || null;
        }
        return null;
      }

      return data[0];
    } catch (err) {
      console.error("Profile fetch error:", err);
      return null;
    }
  };

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const currentUser = session?.user ?? null;
        setUser(currentUser);

        if (currentUser && session?.access_token) {
          const p = await fetchProfile(currentUser.id, session.access_token);
          setProfile(p);
        } else {
          setProfile(null);
        }
        setLoading(false);

        if (event === "SIGNED_IN" && window.location.hash.includes("access_token")) {
          window.history.replaceState(null, "", window.location.pathname);
        }
      }
    );

    // Clean up auth token from URL hash after Supabase has had a chance to parse it
    if (window.location.hash.includes("access_token")) {
      setTimeout(() => {
        window.history.replaceState(null, "", window.location.pathname);
      }, 100);
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      if (currentUser && session?.access_token) {
        fetchProfile(currentUser.id, session.access_token).then((p) => {
          setProfile(p);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    if (!supabase) return;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
      },
    });
    if (error) console.error("Google sign-in error:", error.message);
  };

  const signOut = async () => {
    if (!supabase) return;
    const { error } = await supabase.auth.signOut();
    if (error) console.error("Sign-out error:", error.message);
    setUser(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
